import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Home, MessageCircle, Target, Sparkles, Trophy, ShoppingBag, User, MoreHorizontal, Users, Settings, ChevronRight, BarChart3, LogOut, Clock, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { clearAuthTokens, getPortalType, getUserData, clearAllSessions, getAuthToken } from '@/utils/auth';
import { apiUrl } from '@/api/config';
import { shopAPI } from '@/api/shop';
import axios from 'axios';

const menuItems = [
  { id: 'learn', label: 'LEARN', icon: Home, color: 'text-orange-500', type: 'regular' },
  { id: 'chat', label: 'CHAT', icon: MessageCircle, color: 'text-green-400', type: 'regular' },
  {
    id: 'quests',
    label: 'QUESTS',
    icon: Target,
    color: 'text-orange-400',
    type: 'regular',
  },
  { id: 'curiosity', label: 'CURIOSITY CENTRE', icon: Sparkles, color: 'text-purple-400', type: 'regular' },
  { id: 'community', label: 'COMMUNITY', icon: Users, color: 'text-amber-400', type: 'regular' },
  {
    id: 'achievements',
    label: 'ACHIEVEMENTS',
    icon: Trophy,
    color: 'text-yellow-500',
    type: 'expandable',
    children: [
      { id: 'leaderboard', label: 'LEADERBOARD', icon: Trophy, color: 'text-yellow-400' },
      { id: 'shop', label: 'SHOP', icon: ShoppingBag, color: 'text-red-400' },
    ]
  },
  {
    id: 'more',
    label: 'MORE',
    icon: MoreHorizontal,
    color: 'text-gray-500',
    type: 'expandable',
    children: [
      { id: 'profile', label: 'PROFILE', icon: User, color: 'text-gray-400' },
      { id: 'settings', label: 'SETTINGS', icon: Settings, color: 'text-gray-400' },
      { id: 'about', label: 'ABOUT', icon: Info, color: 'text-teal-400' },
    ]
  },
];

export default function Sidebar({ activeSection, setActiveSection, onNavigate = () => {}, showCloseButton = false, onClose = () => {}, onLogout = () => {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [expandedItem, setExpandedItem] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [equippedItems, setEquippedItems] = useState({
    CHARACTER: null,
    AVATAR: null,
    FRAME: null,
  });

  // Auto-expand "MORE" menu if on profile, settings, or about page
  // Auto-expand "QUESTS" menu if on quests page
  // Auto-expand "CHAT" menu if on chat page
  useEffect(() => {
    if (
      location.pathname.includes('/profile') ||
      location.pathname.includes('/settings') ||
      location.pathname.includes('/about')) {
      setExpandedItem('more');
    } else if (location.pathname.includes('/quests')) {
      setExpandedItem('quests');
    } else if (location.pathname.includes('/leaderboard') || location.pathname.includes('/shop')) {
      setExpandedItem('achievements');
    }
  }, [location.pathname]);

  // Load user profile data
  useEffect(() => {
    const loadUserProfile = () => {
      const portal = getPortalType(window.location.pathname || '');
      const userData = getUserData(portal);
      if (userData) {
        // Get profile photo from backend - check various possible fields
        let photoUrl = null;
        const photoField = userData.profile_photo || userData.profile_pic_url || userData.photo || userData.avatar;

        if (photoField) {
          // If it's a full URL, use it directly
          if (photoField.startsWith('http://') || photoField.startsWith('https://')) {
            photoUrl = photoField;
          } else {
            // If it's a relative path, construct full URL using apiUrl helper
            // Remove leading slash if present for apiUrl
            const cleanPath = photoField.startsWith('/') ? photoField.slice(1) : photoField;
            photoUrl = apiUrl(cleanPath);
          }
        }

        const gradeName = userData.student?.grade?.grade_name || userData.grade?.grade_name || null;
        const gradeNumber = userData.student?.grade?.grade_number || userData.grade?.grade_number || null;

        setUserProfile({
          photo: photoUrl,
          fullName: userData.full_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.username || 'User',
          username: userData.username?.includes('__') ? userData.username.split('__')[1] : (userData.username || ''),
          syllabus: userData.board?.board_name || userData.syllabus || null,
          grade: gradeName,
          gradeNumber: gradeNumber,
          division: userData.division?.division_name || userData.division_display?.division_name || null
        });
      }
    };

    // Load profile on mount and when location changes
    loadUserProfile();

    // Listen for storage events to refresh profile when updated
    const handleStorageChange = () => {
      loadUserProfile();
    };

    // Listen for custom profile update events
    const handleProfileUpdate = () => {
      loadUserProfile();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('profileUpdated', handleProfileUpdate);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, [location.pathname]);

  // Load equipped items for profile display
  useEffect(() => {
    const loadEquippedItems = async () => {
      try {
        const inventoryResponse = await shopAPI.getInventory();
        const inventoryItems = Array.isArray(inventoryResponse)
          ? inventoryResponse
          : (inventoryResponse.results || inventoryResponse);

        // Get equipped items
        const equipped = {
          CHARACTER: inventoryItems.find(inv => inv.is_equipped && inv.shop_item?.item_type === 'CHARACTER') || null,
          AVATAR: inventoryItems.find(inv => inv.is_equipped && inv.shop_item?.item_type === 'AVATAR') || null,
          FRAME: inventoryItems.find(inv => inv.is_equipped && inv.shop_item?.item_type === 'FRAME') || null,
        };
        setEquippedItems(equipped);
      } catch (err) {
        // Silently fail - equipped items are optional
        console.warn('Could not load equipped items for sidebar:', err);
      }
    };

    loadEquippedItems();

    // Listen for equipped items updates
    const handleEquippedItemsUpdate = () => {
      loadEquippedItems();
    };

    window.addEventListener('equippedItemsUpdated', handleEquippedItemsUpdate);
    window.addEventListener('profileUpdated', handleEquippedItemsUpdate);

    return () => {
      window.removeEventListener('equippedItemsUpdated', handleEquippedItemsUpdate);
      window.removeEventListener('profileUpdated', handleEquippedItemsUpdate);
    };
  }, [location.pathname]);

  const handleParentClick = (itemId) => {
    // Toggle accordion - only one open at a time
    setExpandedItem(expandedItem === itemId ? null : itemId);
  };

  const handleChildClick = (childId, parentId) => {
    const portal = getPortalType(window.location.pathname || '');
    const userData = getUserData(portal);
    const userType = userData?.user_type || portal;

    setActiveSection(childId);

    // Navigate to specific routes for children
    if (childId === 'profile') {
      if (userType === 'student') {
        navigate('/student/profile');
      } else if (userType === 'teacher') {
        navigate('/teacher/profile');
      } else if (userType === 'admin') {
        navigate('/admin/profile');
      }
    } else if (childId === 'leaderboard') {
      if (userType === 'student') {
        navigate('/student/achievements/leaderboard');
      }
    } else if (childId === 'shop') {
      if (userType === 'student') {
        navigate('/student/achievements/shop');
      }
    } else if (childId === 'settings') {
      if (userType === 'student') {
        navigate('/student/settings');
      }
    } else if (childId === 'about') {
      if (userType === 'student') {
        navigate('/student/about');
      }
    } else if (childId === 'new-quest' || childId === 'ongoing' || childId === 'completed' || childId === 'drafts') {
      // Quest submenu items - navigate to quests page with tab parameter
      if (userType === 'student') {
        navigate(`/student/quests?tab=${childId}`);
      }
    }
      // Keep parent expanded when child is clicked
      try { onNavigate(); } catch {}
  };

  const handleItemClick = (itemId) => {
    const portal = getPortalType(window.location.pathname || '');
    const userData = getUserData(portal);
    const userType = userData?.user_type || portal;

    // Special handling for routes that need navigation
    if (itemId === 'curiosity') {
      // Only students can access curiosity
      if (userType === 'student') {
        navigate('/student/curiosity');
      } else {
        // Teachers/Admins stay on their dashboard
        setActiveSection('learn');
      }    } else if (itemId === 'community') {
      // Only students can access community
      if (userType === 'student') {
        navigate('/student/community');
      } else {
        setActiveSection('learn');
      }    } else if (itemId === 'chat') {
      // Only students can access chat
      if (userType === 'student') {
        navigate('/student/chat');
      } else {
        setActiveSection('learn');
      }
    } else if (itemId === 'quests') {
      // Only students can access quests
      if (userType === 'student') {
        navigate('/student/quests?tab=new-quest');
      } else {
        setActiveSection('learn');
      }
    } else if (itemId === 'profile') {
      if (userType === 'student') {
        navigate('/student/profile');
      } else if (userType === 'teacher') {
        navigate('/teacher/profile');
      } else if (userType === 'admin') {
        navigate('/admin/profile');
      }
    } else if (itemId === 'learn') {
      // For teachers/admins, stay on their dashboard
      if (userType === 'teacher') {
        navigate('/teacher/dashboard');
      } else if (userType === 'admin') {
        navigate('/admin/dashboard');
      } else {
        // Students use Learn page
        navigate('/student/learn');
      }
    } else {
      // For other sections, use the setActiveSection callback
      // which will be handled by Dashboard
      setActiveSection(itemId);
    }
      // Close overlay when navigating or selecting a non-expandable item
      try {
        const item = menuItems.find(m => m.id === itemId);
        if (!item || item.type !== 'expandable') onNavigate();
      } catch {}
  };

  const isChildActive = (item) => {
    if (item.type === 'expandable' && item.children) {
      return item.children.some(child => child.id === activeSection);
    }
    return false;
  };

  // Check if a quest child item is active based on URL query parameter
  // Use useEffect to ensure searchParams updates trigger re-renders
  const isQuestChildActive = (childId) => {
    // Only check on the main quests page (/student/quests), not sub-pages
    if (location.pathname === '/student/quests') {
      // Get tab from searchParams hook (reactive to URL changes)
      const tabFromUrl = searchParams.get('tab');
      // Default to 'new-quest' if no tab parameter is present (matching QuestsPage behavior)
      const activeTab = tabFromUrl || 'new-quest';
      // Strict comparison to ensure exact match
      return activeTab === childId;
    }
    return false;
  };

  return (
    <div className="min-w-fit w-full max-w-[3.5rem] sm:max-w-xs sm:max-w-sm h-[100dvh] bg-[hsl(var(--sidebar-bg))] sm:bg-[hsl(var(--sidebar-bg))] border-r border-[hsl(var(--card-border))] flex flex-col transition-all duration-300 pb-safe">
      {/* Logo + Brand Name */}
      <div className="p-4 sm:p-6 pb-6 sm:pb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="/logo.png"
            alt="Budlee AI Logo"
            className="h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain flex-shrink-0 animate-logo-jump"
          />
          <div className="flex-1 flex flex-col justify-center">
            <img 
              src="/logo_text.png" 
              alt="Budlee AI" 
              className="h-6 sm:h-7 md:h-8 object-contain w-auto" 
            />
          </div>
        </div>
        {/* Close Button for Mobile */}
        {showCloseButton && (
          <button
            onClick={onClose}
            className="p-1 rounded-full bg-white/90 hover:bg-white shadow z-50 lg:hidden"
            title="Close sidebar"
          >
            <X className="w-4 h-4 text-gray-700" />
          </button>
        )}
      </div>

      {/* Menu Items */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isExpanded = expandedItem === item.id;
          const isActive = activeSection === item.id || isChildActive(item) || (item.id === 'chat' && location.pathname.includes('/chat'));

          return (
            <div key={item.id}>
              {/* Parent Item */}
              <button
                onClick={() => {
                  if (item.type === 'expandable') {
                    handleParentClick(item.id);
                  } else {
                    handleItemClick(item.id);
                  }
                }}
                className={cn(
                  'w-full flex items-center gap-4 px-4 py-3.5 rounded-xl font-medium text-sm uppercase tracking-wide transition-all duration-300 relative overflow-hidden',
                  isActive
                    ? 'bg-[hsl(var(--sidebar-hover))] text-foreground shadow-lg'
                    : 'text-muted-foreground hover:bg-[hsl(var(--sidebar-hover))] hover:text-foreground'
                )}
              >
                {/* Neon gradient border for active state */}
                {isActive && (
                  <div className="absolute inset-0 rounded-xl p-[2px] bg-gradient-to-r from-[hsl(var(--teal-vivid))] via-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] animate-pulse-glow">
                    <div className="w-full h-full bg-[hsl(var(--sidebar-hover))] rounded-xl" />
                  </div>
                )}

                {/* Content */}
                <div className="relative z-10 flex items-center gap-3 sm:gap-4 w-full">
                  <Icon className={cn('w-5 h-5 sm:w-6 sm:h-6', item.color)} />
                  <span className="flex-1 text-left text-xs sm:text-sm">{item.label}</span>

                  {/* Chevron for expandable items */}
                  {item.type === 'expandable' && (
                    <ChevronRight
                      className={cn(
                        'w-4 h-4 transition-transform duration-300',
                        isExpanded && 'rotate-90'
                      )}
                    />
                  )}
                </div>

                {/* Hover glow effect */}
                {!isActive && (
                  <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-[hsl(var(--teal-vivid))]/10 to-[hsl(var(--green-bright))]/10 rounded-xl" />
                )}
              </button>

              {/* Children Items (Dropdown) */}
              {item.type === 'expandable' && item.children && (
                <div
                  className={cn(
                    'overflow-hidden transition-all duration-300 ease-in-out',
                    isExpanded ? 'max-h-96 opacity-100 mt-1' : 'max-h-0 opacity-0'
                  )}
                >
                  <div className="space-y-1 pl-4 pr-0 py-1">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      // For quests children, check URL query parameter; for others, use activeSection
                      const isChildActiveState = item.id === 'quests'
                        ? isQuestChildActive(child.id)
                        : activeSection === child.id;

                      return (
                        <button
                          key={child.id}
                          onClick={() => handleChildClick(child.id, item.id)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm uppercase tracking-wide transition-all duration-300 relative overflow-hidden',
                            isChildActiveState
                              ? 'bg-[hsl(var(--sidebar-hover))] text-foreground shadow-md'
                              : 'text-muted-foreground hover:bg-[hsl(var(--sidebar-hover))]/70 hover:text-foreground'
                          )}
                        >
                          {/* Neon gradient border for active child */}
                          {isChildActiveState && (
                            <div className="absolute inset-0 rounded-lg p-[2px] bg-gradient-to-r from-[hsl(var(--teal-vivid))] via-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] animate-pulse-glow">
                              <div className="w-full h-full bg-[hsl(var(--sidebar-hover))] rounded-lg" />
                            </div>
                          )}

                          {/* Child Content */}
                          <div className="relative z-10 flex items-center gap-2 sm:gap-3 w-full">
                            <ChildIcon className={cn('w-4 h-4 sm:w-5 sm:h-5', child.color)} />
                            <span className="text-xs sm:text-sm">{child.label}</span>
                          </div>

                          {/* Hover glow effect for children */}
                          {!isChildActiveState && (
                            <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-[hsl(var(--teal-vivid))]/10 to-[hsl(var(--green-bright))]/10 rounded-lg" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User Profile Section */}
      {userProfile && (
        <div className="p-3 border-t border-[hsl(var(--card-border))] space-y-3">
          {/* Profile Photo, Name, and Logout Icon */}
          <div className="flex items-center justify-between gap-3 px-2">
            <button
              onClick={() => {
                const portal = getPortalType(window.location.pathname || '');
                const userData = getUserData(portal);
                const userType = userData?.user_type || portal;
                if (userType === 'student') {
                  navigate('/student/profile');
                } else if (userType === 'teacher') {
                  navigate('/teacher/profile');
                } else if (userType === 'admin') {
                  navigate('/admin/profile');
                }
                if (onNavigate) onNavigate();
              }}
              className="flex items-center gap-3 min-w-0 hover:opacity-80 transition-opacity cursor-pointer text-left"
              title="View Profile"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] flex items-center justify-center overflow-visible flex-shrink-0 relative">
                {/* Avatar/Profile Photo - Base Layer */}
                {(() => {
                  const portal = getPortalType(window.location.pathname || '');
                  const userData = getUserData(portal);
                  const useProfilePicAsAvatar = userData?.use_profile_pic_as_avatar || false;
                  const imageToShow = (useProfilePicAsAvatar && userProfile.photo)
                    ? userProfile.photo
                    : (equippedItems.AVATAR?.shop_item?.image_url_display || null);
                  const hasImage = !!imageToShow;

                  return (
                    <div className="absolute inset-0 w-full h-full rounded-full overflow-hidden z-0">
                      {hasImage ? (
                        <img
                          src={imageToShow}
                          alt={userProfile.fullName}
                          className="w-full h-full object-cover"
                        />
                      ) : null}
                      <div className={cn(
                        "absolute inset-0 w-full h-full items-center justify-center",
                        hasImage ? "hidden" : "flex"
                      )}>
                        <User className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  );
                })()}
                {/* Profile Frame Overlay */}
                {equippedItems.FRAME?.shop_item?.image_url_display && (
                  <div className="absolute -inset-3 rounded-full overflow-visible pointer-events-none z-10">
                    <img
                      src={equippedItems.FRAME.shop_item.image_url_display}
                      alt="Frame"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex flex-col items-start text-left">
                <p className="text-sm font-semibold text-foreground truncate capitalize w-full text-left">{userProfile.username}</p>
                {userProfile.grade && (
                  <p className="text-[11px] text-muted-foreground truncate text-left">{userProfile.grade}</p>
                )}
              </div>
            </button>
            {/* Logout Icon Button */}
            <button
              onClick={onLogout}
              className={cn(
                'p-2 rounded-lg transition-all duration-300',
                'text-red-400 hover:bg-red-500/10 hover:text-red-300'
              )}
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Logout Icon (if no user profile) */}
      {!userProfile && (
        <div className="p-3 border-t border-[hsl(var(--card-border))] flex justify-end">
          <button
            onClick={onLogout}
            className={cn(
              'p-2 rounded-lg transition-all duration-300',
              'text-red-400 hover:bg-red-500/10 hover:text-red-300'
            )}
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      )}

    </div>
  );
}